import "components/recipe/Recipe.css";
import { RecipeContext } from "components/recipe/providers/RecipeProvider";
import { useAuthentication } from "authentication/AuthenticationContext";
import { Link } from "react-router-dom";
import BaseButton from "base/BaseButton";
import AddRecipeButton from "components/recipe/AddRecipeButton";
import loadingLogo from "images/BlogFreeRecipeLogo.png";
import React, { useEffect, useMemo, useContext, useState } from "react";
import RecipeGridItem from "components/recipe/RecipeGridItem";
import recipesToItemListJsonLd from "searchOptimizations/recipesToItemListJsonLd";
import { flushSync } from "react-dom";

const RecipeList = ({
  onAdd,
  recipes = [],
  isLoading = false,
  recipeView,
  onRecipeViewChange,
}) => {
  const { featureToggles = [] } = useContext(RecipeContext);
  const { user } = useAuthentication();
  const isMyRecipes = recipeView === "mine";
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src="https://assets.pinterest.com/js/pinit.js"], ' +
        'script[src="//assets.pinterest.com/js/pinit.js"]',
    );

    const buildPinterestButtons = () => {
      if (typeof window.doBuild === "function") {
        window.doBuild();
      }

      const buttons = document.getElementById("pin-it-buttons");

      if (buttons && typeof window.parsePinBtns === "function") {
        window.parsePinBtns(buttons);
      }
    };

    if (existingScript) {
      existingScript.addEventListener("load", buildPinterestButtons);
      buildPinterestButtons();

      return () => {
        existingScript.removeEventListener("load", buildPinterestButtons);
      };
    }

    const script = document.createElement("script");
    script.async = true;
    script.type = "text/javascript";
    script.dataset.pinBuild = "doBuild";
    script.src = "https://assets.pinterest.com/js/pinit.js";
    script.addEventListener("load", buildPinterestButtons);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", buildPinterestButtons);
    };
  }, []);

  const itemListJsonLd = useMemo(
    () => recipesToItemListJsonLd(recipes, "https://www.blogfreerecipe.com"),
    [recipes],
  );

  const amazonLinkStatus = useMemo(
    () => featureToggles.find((f) => f.name.includes("Amazon"))?.isEnabled,
    [featureToggles],
  );

  const toggle = (id) => {
    const isOpening = expandedId !== id;
    const update = () => setExpandedId((cur) => (cur === id ? null : id));

    const scrollToCard = () => {
      if (isOpening) {
        document
          .getElementById(`recipe-${id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    const isMobile = window.matchMedia("(max-width: 600px)").matches;

    if (isMobile || !document.startViewTransition) {
      update();
      requestAnimationFrame(scrollToCard);
      return;
    }

    const transition = document.startViewTransition(() => flushSync(update));
    transition.finished.then(scrollToCard);
  };

  const handleAddRecipe = () => {
    onAdd();
  };

  return (
    <>
      {!isLoading && recipes.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(itemListJsonLd).replace(/</g, "\\u003c"),
          }}
        />
      )}

      <section
        className="Recipe-Homepage"
        aria-labelledby="recipe-list-heading"
      >
        <header className="Recipe-Homepage-Intro">
          <h2 id="recipe-list-heading" className="Sub-Heading">
            For those who ... <span className="TLDR">TLDR</span>
          </h2>
          <p>Find your next favorite. Get straight to the recipe.</p>
        </header>

        <div
          role="group"
          aria-label="Recipe collection"
          className="recipe-tabs"
        >
          <BaseButton
            buttonType="button"
            buttonText="All Recipes"
            buttonClass={`recipe-tab ${
              !isMyRecipes ? "recipe-tab--active" : ""
            }`}
            isUnstyled
            ariaPressed={!isMyRecipes}
            handleClick={() => onRecipeViewChange("all")}
          />

          <BaseButton
            buttonType="button"
            buttonText="My Recipes"
            buttonClass={`recipe-tab ${
              isMyRecipes ? "recipe-tab--active" : ""
            }`}
            isUnstyled
            ariaPressed={isMyRecipes}
            handleClick={() => onRecipeViewChange("mine")}
          />
        </div>
        {amazonLinkStatus && (
          <div className="Justify-Center marg-b-1">
            <iframe
              src="https://rcm-na.amazon-adsystem.com/e/cm?o=1&p=42&l=ur1&category=kitchen&banner=09RE3AASKRA6TA2H9K82&f=ifr&linkID=fcf06002557fde8c7826d4f6b2d6cb16&t=blogfreerecip-20&tracking_id=blogfreerecip-20"
              width="234"
              height="60"
              style={{ border: 0 }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
              title="Kitchen advertisement"
            />
          </div>
        )}

        {isMyRecipes && !user ? (
          <div className="recipe-collection-empty">
            <h2>Your recipes, in one place</h2>
            <p>Sign in to view and store your own recipes.</p>
            <Link to="/login">Sign in</Link>
            {" · "}
            <Link to="/signup">Create an account</Link>
          </div>
        ) : isMyRecipes && recipes.length === 0 ? (
          <div className="recipe-collection-empty">
            <h2>No recipes to show</h2>
            <p>Add your first recipe, or check your current filters.</p>
            <BaseButton
              buttonType="button"
              buttonText="+ Add Recipe"
              handleClick={handleAddRecipe}
            />
          </div>
        ) : isLoading ? (
          <div className="Recipe-List-Loading" role="status">
            <img
              className="loading Recipe-List-LoadingLogo"
              src={loadingLogo}
              alt="Loading recipes…"
            />
          </div>
        ) : recipes.length === 0 ? (
          <div className="recipe-collection-empty">
            <p>No recipes match your search or filters.</p>
          </div>
        ) : (
          <div aria-busy={isLoading}>
            {!isLoading && recipes.length > 0 && (
              <div className="Recipe-Grid">
                {recipes.map((recipe) => (
                  <RecipeGridItem
                    key={recipe.id}
                    recipe={recipe}
                    isExpanded={expandedId === recipe.id}
                    onToggle={toggle}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <AddRecipeButton onAdd={handleAddRecipe} />
    </>
  );
};

export default RecipeList;
